import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";

// Create base store configuration for contacts
const contactsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/contacts",
  name: "ContactsStoreRefactor",
  objectName: "Contact", // Auto-generates CRUD success messages
  enableCache: true, // Enable caching
  actionCableChannel: null, // No real-time updates needed
  infiniteLoading: false, // Simple list, no pagination needed
});

export const useContactsStoreRefactor = defineStore("contactsRefactor", {
  ...contactsBaseStore,

  state: () => ({
    ...contactsBaseStore.state(),

    // Enhanced contact type configuration
    icons: {
      organization: { icon: "mdi-office-building", color: "blue" },
      individual: { icon: "mdi-account-box", color: "green" },
      department: { icon: "mdi-charity", color: "amber" },
    },

    // Phone type configuration
    phoneTypes: {
      mobile: { label: "Mobiel", icon: "mdi-cellphone", color: "green" },
      vast: { label: "Vast", icon: "mdi-phone", color: "blue" },
      fax: { label: "Fax", icon: "mdi-fax", color: "orange" },
      overig: { label: "Overig", icon: "mdi-phone-outline", color: "grey" },
    },

    // Search and filter state
    searchTerm: "",
    activeFilters: {
      types: [], // organization, department, individual
      hasEmail: null,
      hasPhone: null,
      hasAddress: null,
      categoryIds: [], // contact category IDs
    },
  }),

  getters: {
    ...contactsBaseStore.getters,

    /**
     * Get all contacts (maps to state.data for compatibility)
     */
    contacts: (state) => {
      return state.data || [];
    },

    /**
     * Backward compatibility - same as contacts
     */
    entities: (state) => {
      return state.data || [];
    },

    /**
     * Get contact by ID
     */
    getContactById: (state) => (id) => {
      return state.data.find((contact) => contact.id == id);
    },

    /**
     * Get contacts by type
     */
    getContactsByType: (state) => (type) => {
      return state.data.filter((contact) => contact.contact_type === type);
    },

    /**
     * Get filtered and searched contacts
     */
    filteredContacts: (state) => {
      let results = state.data || [];

      // Apply type filters
      if (state.activeFilters.types.length > 0) {
        results = results.filter((contact) =>
          state.activeFilters.types.includes(contact.type),
        );
      }

      // Apply search term
      if (state.searchTerm.trim()) {
        const searchLower = state.searchTerm.toLowerCase();
        results = results.filter((contact) => {
          const name = (contact.name || "").toLowerCase();
          const primaryEmail = (
            contact.primary_email_address || ""
          ).toLowerCase();
          const primaryPhone = (
            contact.primary_phone_formatted || ""
          ).toLowerCase();

          return (
            name.includes(searchLower) ||
            primaryEmail.includes(searchLower) ||
            primaryPhone.includes(searchLower)
          );
        });
      }

      // Apply contact detail filters
      if (state.activeFilters.hasEmail !== null) {
        results = results.filter((contact) => {
          const hasEmail = !!(contact.emails && contact.emails.length > 0);
          return hasEmail === state.activeFilters.hasEmail;
        });
      }

      if (state.activeFilters.hasPhone !== null) {
        results = results.filter((contact) => {
          const hasPhone = !!(
            contact.phone_numbers && contact.phone_numbers.length > 0
          );
          return hasPhone === state.activeFilters.hasPhone;
        });
      }

      if (state.activeFilters.hasAddress !== null) {
        results = results.filter((contact) => {
          const hasAddress = !!(
            contact.addresses && contact.addresses.length > 0
          );
          return hasAddress === state.activeFilters.hasAddress;
        });
      }

      // Apply category filters
      if (
        state.activeFilters.categoryIds &&
        state.activeFilters.categoryIds.length > 0
      ) {
        results = results.filter((contact) => {
          const contactCategoryIds = contact.contact_category_ids || [];
          // Check if contact has ANY of the selected categories
          return state.activeFilters.categoryIds.some((catId) =>
            contactCategoryIds.includes(catId),
          );
        });
      }

      // Sort results by name
      return results.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },

    /**
     * Predicate: does a single contact match the active search + filters?
     * Extracted so the hierarchy tree can decide per-node visibility while
     * still keeping ancestors of matches visible for context.
     * @returns {(contact: object) => boolean}
     */
    contactMatchesFilters: (state) => (contact) => {
      // Type filter
      if (
        state.activeFilters.types.length > 0 &&
        !state.activeFilters.types.includes(contact.type)
      ) {
        return false;
      }

      // Search term (name, primary email/phone, and location for disambiguation)
      if (state.searchTerm.trim()) {
        const s = state.searchTerm.toLowerCase();
        const haystack = [
          contact.name,
          contact.display_name,
          contact.primary_email_address,
          contact.primary_phone_formatted,
          contact.primary_location_address,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(s)) return false;
      }

      // Contact-detail filters
      if (state.activeFilters.hasEmail !== null) {
        const hasEmail = !!(contact.emails && contact.emails.length > 0);
        if (hasEmail !== state.activeFilters.hasEmail) return false;
      }
      if (state.activeFilters.hasPhone !== null) {
        const hasPhone = !!(
          contact.phone_numbers && contact.phone_numbers.length > 0
        );
        if (hasPhone !== state.activeFilters.hasPhone) return false;
      }
      if (state.activeFilters.hasAddress !== null) {
        const hasAddress = !!(contact.addresses && contact.addresses.length > 0);
        if (hasAddress !== state.activeFilters.hasAddress) return false;
      }

      // Category filter
      if (
        state.activeFilters.categoryIds &&
        state.activeFilters.categoryIds.length > 0
      ) {
        const ids = contact.contact_category_ids || [];
        if (!state.activeFilters.categoryIds.some((c) => ids.includes(c))) {
          return false;
        }
      }

      return true;
    },

    /**
     * Number of contacts that directly match the active search + filters
     * (used for the results footer and the empty-state message).
     */
    matchedContactsCount: (state) => {
      const data = state.data || [];
      return data.filter((c) => state.contactMatchesFilters(c)).length;
    },

    /**
     * Whether any search term or filter is currently narrowing the list.
     */
    isFiltering: (state) => {
      return (
        state.searchTerm.trim() !== "" ||
        state.activeFilters.types.length > 0 ||
        state.activeFilters.hasEmail !== null ||
        state.activeFilters.hasPhone !== null ||
        state.activeFilters.hasAddress !== null ||
        (state.activeFilters.categoryIds &&
          state.activeFilters.categoryIds.length > 0)
      );
    },

    /**
     * Build the contact hierarchy client-side from `parent_id`.
     *
     * Organisations sit at the top with their departments and people nested
     * beneath them. A node is included when it matches the active filters OR
     * has a matching descendant, so a search for a person still shows the
     * organisation → department context that disambiguates colliding names.
     *
     * Each tree node: { contact, children, matched, contextOnly }
     * - matched: the contact itself passes the filters (rendered normally)
     * - contextOnly: shown only because a descendant matched (rendered dimmed)
     */
    contactTree: (state) => {
      const data = state.data || [];
      if (data.length === 0) return [];

      const byId = new Map(data.map((c) => [c.id, c]));
      const childrenOf = new Map();
      const roots = [];

      for (const contact of data) {
        const parent =
          contact.parent_id != null ? byId.get(contact.parent_id) : null;
        if (parent) {
          if (!childrenOf.has(parent.id)) childrenOf.set(parent.id, []);
          childrenOf.get(parent.id).push(contact);
        } else {
          // No parent, or parent not in the dataset -> treat as a root
          roots.push(contact);
        }
      }

      const byName = (a, b) =>
        (a.name || "").localeCompare(b.name || "", "nl", {
          sensitivity: "base",
        });

      const buildNode = (contact) => {
        const kids = (childrenOf.get(contact.id) || [])
          .sort(byName)
          .map(buildNode)
          .filter(Boolean);

        const matched = state.contactMatchesFilters(contact);

        // Prune branches with no match anywhere
        if (!matched && kids.length === 0) return null;

        return {
          contact,
          children: kids,
          matched,
          contextOnly: !matched && kids.length > 0,
        };
      };

      return roots
        .sort(byName)
        .map(buildNode)
        .filter(Boolean);
    },

    /**
     * Count contacts by type
     */
    contactCounts: (state) => {
      const data = state.data || [];
      return {
        organization: data.filter((c) => c.type === "organization").length,
        department: data.filter((c) => c.type === "department").length,
        individual: data.filter((c) => c.type === "individual").length,
        total: data.length,
      };
    },

    /**
     * Get active filter count
     */
    activeFilterCount: (state) => {
      let count = 0;

      if (
        state.activeFilters.types.length > 0 &&
        state.activeFilters.types.length < 3
      ) {
        count++;
      }

      if (state.activeFilters.hasEmail !== null) count++;
      if (state.activeFilters.hasPhone !== null) count++;
      if (state.activeFilters.hasAddress !== null) count++;
      if (state.activeFilters.categoryIds?.length > 0)
        count += state.activeFilters.categoryIds.length;

      return count;
    },

    /**
     * Get phone type configuration
     */
    getPhoneTypeConfig: (state) => (type) => {
      return state.phoneTypes[type] || state.phoneTypes.overig;
    },
  },

  actions: {
    ...contactsBaseStore.actions,

    /**
     * Set search term
     */
    setSearchTerm(term) {
      this.searchTerm = term || "";
    },

    /**
     * Set type filters
     */
    setTypeFilters(types) {
      this.activeFilters.types = types || [];
    },

    /**
     * Set contact detail filters
     */
    setContactDetailFilters(filters) {
      this.activeFilters = {
        ...this.activeFilters,
        ...filters,
      };
    },

    /**
     * Clear all filters
     */
    clearFilters() {
      this.searchTerm = "";
      this.activeFilters = {
        types: [],
        hasEmail: null,
        hasPhone: null,
        hasAddress: null,
        categoryIds: [],
      };
    },

    /**
     * Quick filter shortcuts
     */
    filterByType(type) {
      this.activeFilters.types = [type];
    },

    /**
     * Set icons for different contact types
     */
    setIcons(icons) {
      this.icons = icons;
    },

    /**
     * Set phone type configuration
     */
    setPhoneTypes(phoneTypes) {
      this.phoneTypes = phoneTypes;
    },

    // All CRUD methods are fully inherited from base store
    // Auto-generated messages use "Contact" from objectName
  },
});
